import React, { useState, useEffect, useMemo } from 'react';
import { useSkipsData } from '../context/SkipsDataContext';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { exportLogToCSV, exportLogToPDF } from '../utils/exportUtils';
import { parseJobDate } from '../utils/dateUtils';
import toast from 'react-hot-toast';
import ViewToggle from './ViewToggle';
import { WASTE_TYPES } from '../constants/jobOptions';

const LogPage = () => {
    const { jobs, companyName, companyId } = useSkipsData();
    const navigate = useNavigate();
    const [timeRange, setTimeRange] = useState('today'); // 'today', '3days', '7days'
    const [filters, setFilters] = useState({
        actionType: 'all', // 'all', 'delivery', 'collection'
        driver: '',
        size: '',
        wasteType: '',
        paymentStatus: ['Paid', 'TO PAY', 'Account', 'Unknown']
    });
    const [userMap, setUserMap] = useState({}); // Map userId to driverName

    // Fetch user names for collectedBy lookups
    useEffect(() => {
        const fetchUsers = async () => {
            if (!companyId) return;
            try {
                const usersRef = collection(db, 'users');
                const q = query(usersRef, where('companyId', '==', companyId));
                const usersSnapshot = await getDocs(q);
                const map = {};
                usersSnapshot.forEach(doc => {
                    const data = doc.data();
                    const isAdmin = data.admin === true || data.isAdmin === true || data.role === 'admin';
                    const userName = isAdmin
                        ? 'Admin'
                        : (data.displayName || data.name || data.email?.split('@')[0] || 'Unknown');
                    map[doc.id] = userName;
                });
                setUserMap(map);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        fetchUsers();
    }, [companyId]);

    // Transform jobs into events
    const events = useMemo(() => {
        const eventList = [];
        const currentTime = new Date();

        // Calculate time range cutoff
        let cutoffTime;
        switch (timeRange) {
            case 'today':
                cutoffTime = new Date(currentTime);
                cutoffTime.setHours(0, 0, 0, 0);
                break;
            case '3days':
                cutoffTime = new Date(currentTime.getTime() - (3 * 24 * 60 * 60 * 1000));
                break;
            case '7days':
                cutoffTime = new Date(currentTime.getTime() - (7 * 24 * 60 * 60 * 1000));
                break;
            default:
                cutoffTime = new Date(0);
        }

        jobs.forEach(job => {
            // Delivery event - show for all jobs that have been delivered (delivered or collected status)
            // This ensures we always show the delivery event even after collection
            if ((job.status === 'delivered' || job.status === 'collected') && job.timestamp) {
                const deliveryTime = parseJobDate(job.initialTime, job.timestamp);
                if (deliveryTime >= cutoffTime) {
                    eventList.push({
                        id: `${job.id}-delivery`,
                        type: 'delivery',
                        timestamp: deliveryTime,
                        driver: job.driverName || 'Unknown',
                        jobId: job.skipId,
                        address: job.address?.displayAddress || job.streetAddress || 'N/A',
                        size: job.size,
                        wasteType: job.wasteType || job.description,
                        paymentStatus: job.paymentStatus || 'Unknown',
                        invoiced: job.invoiced === true,
                        jobType: job.jobType,
                        originalJob: job,
                        linkedJobId: job.jobType === 'Exchange' ? job.skipId : null
                    });
                }
            }

            // Collection event - only for jobs with 'collected' status
            if (job.status === 'collected') {
                const collectionTime = job.collectionTimestamp
                    ? parseJobDate(null, job.collectionTimestamp)
                    : parseJobDate(job.initialTime, job.timestamp);

                if (collectionTime && collectionTime >= cutoffTime) {
                    const collectedByName = job.collectedBy ? (userMap[job.collectedBy] || 'Admin') : 'Admin';

                    eventList.push({
                        id: `${job.id}-collection`,
                        type: 'collection',
                        timestamp: collectionTime,
                        driver: collectedByName,
                        jobId: job.skipId,
                        address: job.address?.displayAddress || job.streetAddress || 'N/A',
                        size: job.size,
                        wasteType: job.wasteType || job.description,
                        paymentStatus: job.paymentStatus || 'Unknown',
                        invoiced: job.invoiced === true,
                        jobType: job.jobType,
                        originalJob: job,
                        linkedJobId: job.jobType === 'Exchange' ? job.skipId : null
                    });
                }
            }
        });

        // Sort by timestamp descending (newest first)
        // If timestamps are equal, collection should appear before delivery (since it logically happened after)
        eventList.sort((a, b) => {
            const timeDiff = b.timestamp - a.timestamp;
            if (timeDiff !== 0) {
                return timeDiff;
            }
            // When timestamps are equal, prioritize collection over delivery
            if (a.type === 'collection' && b.type === 'delivery') return -1;
            if (a.type === 'delivery' && b.type === 'collection') return 1;
            return 0;
        });

        return eventList;
    }, [jobs, timeRange, userMap]);

    // Apply filters to events
    const filteredEvents = useMemo(() => {
        return events.filter(event => {
            // Action type filter
            if (filters.actionType !== 'all' && event.type !== filters.actionType) {
                return false;
            }

            // Driver filter
            if (filters.driver && event.driver !== filters.driver) {
                return false;
            }

            // Size filter
            if (filters.size && event.size !== filters.size) {
                return false;
            }

            // Waste type filter
            if (filters.wasteType && event.wasteType !== filters.wasteType) {
                return false;
            }

            // Payment status filter
            if (!filters.paymentStatus.includes(event.paymentStatus)) {
                return false;
            }

            return true;
        });
    }, [events, filters]);

    // Get unique values for filters
    const uniqueDrivers = useMemo(() => {
        const drivers = new Set();
        events.forEach(event => {
            if (event.driver) drivers.add(event.driver);
        });
        return Array.from(drivers).sort();
    }, [events]);

    const uniqueSizes = useMemo(() => {
        const sizes = new Set();
        events.forEach(event => {
            if (event.size) sizes.add(event.size);
        });
        return Array.from(sizes).sort();
    }, [events]);

    const uniqueWasteTypes = WASTE_TYPES;

    const handleViewOnMap = (event) => {
        if (event.type === 'delivery' && event.originalJob) {
            const job = event.originalJob;

            // Validate coordinates
            if (!job.position || !Array.isArray(job.position) || job.position.length !== 2) {
                toast.error('This job has no location coordinates');
                return;
            }

            const [lat, lng] = job.position;

            // Validate that coordinates are valid numbers
            if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
                toast.error('Invalid coordinates for this job');
                return;
            }

            // Store navigation data in sessionStorage
            sessionStorage.setItem('focusJobId', job.id);
            sessionStorage.setItem('mapCenter', JSON.stringify({
                lat: lat,
                lng: lng,
                zoom: 16
            }));

            // Navigate to map
            toast.success('Opening map view...');
            navigate('/map');
        }
    };

    const handleExportCSV = () => {
        exportLogToCSV(filteredEvents, companyName);
    };

    const handleExportPDF = () => {
        exportLogToPDF(filteredEvents, companyName);
    };

    const formatTimestamp = (timestamp) => {
        return timestamp.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw' }}>
            {/* Fixed Header with View Toggle */}
            <ViewToggle />

            {/* Header */}
            <div style={{
                padding: '20px',
                backgroundColor: '#fff',
                borderBottom: '1px solid #e5e7eb',
                marginTop: '50px'
            }}>
                <div style={{ marginBottom: '16px' }}>
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Activity Log</h1>
                </div>

                {/* Time Range Buttons */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    {[
                        { value: 'today', label: 'Today' },
                        { value: '3days', label: 'Last 3 Days' },
                        { value: '7days', label: 'Last 7 Days' }
                    ].map(option => (
                        <button
                            key={option.value}
                            onClick={() => setTimeRange(option.value)}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: timeRange === option.value ? '#166534' : '#fff',
                                color: timeRange === option.value ? '#fff' : '#57534E',
                                border: `1px solid ${timeRange === option.value ? '#166534' : '#D6D3D1'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: '14px'
                            }}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                {/* Export Buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={handleExportCSV}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#166534',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            fontSize: '14px'
                        }}
                    >
                        Export CSV
                    </button>
                    <button
                        onClick={handleExportPDF}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#166534',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            fontSize: '14px'
                        }}
                    >
                        Export PDF
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Filter Sidebar */}
                <div style={{
                    width: '280px',
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRight: '1px solid #E7E5E4',
                    overflowY: 'auto'
                }}>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', color: '#292524' }}>Filters</h3>

                    {/* Action Filter */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                            Action
                        </label>
                        <select
                            value={filters.actionType}
                            onChange={(e) => setFilters({ ...filters, actionType: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px'
                            }}
                        >
                            <option value="all">All</option>
                            <option value="delivery">Deliveries Only</option>
                            <option value="collection">Collections Only</option>
                        </select>
                    </div>

                    {/* Driver Filter */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                            Driver
                        </label>
                        <select
                            value={filters.driver}
                            onChange={(e) => setFilters({ ...filters, driver: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px'
                            }}
                        >
                            <option value="">All</option>
                            {uniqueDrivers.map((driver, index) => (
                                <option key={index} value={driver}>{driver}</option>
                            ))}
                        </select>
                    </div>

                    {/* Size Filter */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                            Size
                        </label>
                        <select
                            value={filters.size}
                            onChange={(e) => setFilters({ ...filters, size: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px'
                            }}
                        >
                            <option value="">All</option>
                            {uniqueSizes.map((size, index) => (
                                <option key={index} value={size}>{size}</option>
                            ))}
                        </select>
                    </div>

                    {/* Waste Filter */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                            Waste
                        </label>
                        <select
                            value={filters.wasteType}
                            onChange={(e) => setFilters({ ...filters, wasteType: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px'
                            }}
                        >
                            <option value="">All</option>
                            {uniqueWasteTypes.map((type, index) => (
                                <option key={index} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    {/* Payment Filter */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                            Payment
                        </label>
                        {['Paid', 'TO PAY', 'Account', 'Unknown'].map((status) => (
                            <label key={status} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                <input
                                    type="checkbox"
                                    checked={filters.paymentStatus.includes(status)}
                                    onChange={(e) => {
                                        const newPaymentStatus = e.target.checked
                                            ? [...filters.paymentStatus, status]
                                            : filters.paymentStatus.filter(s => s !== status);
                                        setFilters({ ...filters, paymentStatus: newPaymentStatus });
                                    }}
                                    style={{ marginRight: '8px' }}
                                />
                                <span style={{
                                    fontWeight: status === 'TO PAY' ? 'bold' : 'normal',
                                    color: status === 'TO PAY' ? '#EF4444' : '#374151'
                                }}>
                                    {status}
                                </span>
                            </label>
                        ))}
                    </div>

                    {/* Clear Filters */}
                    <button
                        onClick={() => setFilters({
                            actionType: 'all',
                            driver: '',
                            size: '',
                            wasteType: '',
                            paymentStatus: ['Paid', 'TO PAY', 'Account', 'Unknown']
                        })}
                        style={{
                            width: '100%',
                            padding: '8px 16px',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Clear
                    </button>
                </div>

                {/* Table */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                    <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                        Showing {filteredEvents.length} {filteredEvents.length === 1 ? 'event' : 'events'}
                    </p>

                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Timestamp</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Action</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Driver</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Address</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Size</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Waste</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Payment</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Job ID</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEvents.map((event) => (
                                <tr key={event.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '12px' }}>{formatTimestamp(event.timestamp)}</td>
                                    <td style={{ padding: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                backgroundColor: event.type === 'delivery' ? '#DBEAFE' : '#D1FAE5',
                                                color: event.type === 'delivery' ? '#1E40AF' : '#065F46'
                                            }}>
                                                {event.type === 'delivery' ? 'Delivery' : 'Collection'}
                                            </span>
                                            {event.linkedJobId && (
                                                <span style={{ fontSize: '12px', color: '#9ca3af' }} title="Part of Exchange">
                                                    🔗
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px' }}>{event.driver}</td>
                                    <td style={{ padding: '12px' }}>{event.address}</td>
                                    <td style={{ padding: '12px' }}>{event.size}</td>
                                    <td style={{ padding: '12px' }}>{event.wasteType}</td>
                                    <td style={{ padding: '12px' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            <span style={{
                                                fontWeight: event.paymentStatus === 'TO PAY' ? 'bold' : 'normal',
                                                color: event.paymentStatus === 'TO PAY' ? '#EF4444' : '#374151'
                                            }}>
                                                {event.paymentStatus}
                                            </span>
                                            {event.invoiced && (
                                                <span style={{
                                                    padding: '1px 4px',
                                                    borderRadius: '3px',
                                                    fontSize: '9px',
                                                    fontWeight: '600',
                                                    backgroundColor: '#DBEAFE',
                                                    color: '#1E40AF'
                                                }}>
                                                    INV
                                                </span>
                                            )}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '11px', fontFamily: 'monospace' }}>
                                        {event.jobId || 'N/A'}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        {event.type === 'delivery' && (
                                            <button
                                                onClick={() => handleViewOnMap(event)}
                                                style={{
                                                    padding: '4px 12px',
                                                    fontSize: '12px',
                                                    backgroundColor: '#166534',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                View on Map
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {filteredEvents.length === 0 && (
                        <div style={{
                            padding: '40px',
                            textAlign: 'center',
                            color: '#9ca3af'
                        }}>
                            No events found for the selected filters and time range.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LogPage;
